import { useEffect, useMemo, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { Link as RouterLink } from 'react-router-dom'
import { Banner, Button, Input, Loader, Select, Tabs, cn } from '@cloudflare/kumo'
import type { BuildingHours, FreeRoomView, RoomBookingDto } from '@hub-wifi/shared'
import { ApiError } from '../../api/client'
import { createMyBooking } from '../../api/bookings'
import { listFreeRooms } from '../../api/rooms'
import { describeBookingError } from '../booking-form/utils'
import { useTranslation } from '../../i18n'
import { formatDuration } from '../../lib/format'
import {
  addDaysInTz,
  formatHourMinute,
  isoAtBuildingTz,
  todayYmdInTz,
  wallClockToUtc,
} from '../../lib/calendar-time'
import {
  DEFAULT_DURATION,
  DURATIONS,
  nowStart,
  sortRoomsByFit,
  startSlots,
  type WhenMode,
} from './book-sheet'
import { HomeSheet } from './HomeSheet'

/** The sheet asks once per start time with the shortest chip and filters the
 *  chips locally from each room's `freeUntil`, so switching duration is instant. */
const FETCH_MINUTES = DURATIONS[0]
/** Bookings can be made up to 180 days ahead (booking service rule). */
const MAX_DAYS_AHEAD = 180

export interface BookSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  timeZone: string
  hours: BuildingHours[]
  now: Date
  onBooked: (booking: RoomBookingDto) => void
}

type Feedback = { kind: 'conflict' } | { kind: 'error'; message: string } | null

/** Rooms Exchange declined for one start + duration. `/rooms/free` only sees
 *  hub bookings, so after a 409 it can offer the same room again. */
interface Declined {
  key: string
  roomIds: string[]
}

const declinedKey = (startIso: string, minutes: number) => `${startIso}|${minutes}`

function isInvalidTimeRange(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    (err.body as { code?: string } | undefined)?.code === 'INVALID_TIME_RANGE'
  )
}

/** "Book a room": when, how long, and the rooms that fit, booked in one tap. */
export function BookSheet({ open, onOpenChange, timeZone, hours, now, onBooked }: BookSheetProps) {
  const { t } = useTranslation()
  return (
    <HomeSheet open={open} onOpenChange={onOpenChange} title={t('dashboard.bookSheet.title')}>
      <BookSheetBody timeZone={timeZone} hours={hours} now={now} onBooked={onBooked} />
    </HomeSheet>
  )
}

function BookSheetBody({
  timeZone,
  hours,
  now,
  onBooked,
}: Omit<BookSheetProps, 'open' | 'onOpenChange'>) {
  const { t } = useTranslation()
  const { getAccessTokenSilently } = useAuth0()
  const today = todayYmdInTz(timeZone, now)
  const tomorrow = addDaysInTz(today, 1, timeZone)

  const [mode, setMode] = useState<WhenMode>('now')
  const [minutes, setMinutes] = useState<number>(DEFAULT_DURATION)
  const [laterHm, setLaterHm] = useState<string>('')
  const [dateYmd, setDateYmd] = useState<string>(tomorrow)
  const [dateHm, setDateHm] = useState<string>('')
  const [rooms, setRooms] = useState<FreeRoomView[] | null>(null)
  const [buildingOpen, setBuildingOpen] = useState(true)
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [reloadKey, setReloadKey] = useState(0)
  const [bookingRoomId, setBookingRoomId] = useState<string | null>(null)
  const [feedback, setFeedback] = useState<Feedback>(null)
  const [declined, setDeclined] = useState<Declined>({ key: '', roomIds: [] })

  const laterSlots = useMemo(() => {
    // "Later today" starts after the "Now" slot.
    const slots = startSlots(hours, today, timeZone, now)
    return slots.slice(1)
  }, [hours, today, timeZone, now])
  const dateSlots = useMemo(
    () => (dateYmd ? startSlots(hours, dateYmd, timeZone, now) : []),
    [hours, dateYmd, timeZone, now],
  )

  // Keep the picked time valid as the options change (date switch, clock tick).
  const laterValue = laterSlots.includes(laterHm) ? laterHm : (laterSlots[3] ?? laterSlots[0] ?? '')
  const dateValue = dateSlots.includes(dateHm) ? dateHm : (dateSlots[0] ?? '')

  const start: { ymd: string; hm: string } | null =
    mode === 'now'
      ? nowStart(timeZone, now)
      : mode === 'later'
        ? laterValue
          ? { ymd: today, hm: laterValue }
          : null
        : dateYmd && dateValue
          ? { ymd: dateYmd, hm: dateValue }
          : null
  const fromMs = start ? wallClockToUtc(start.ymd, start.hm, timeZone).getTime() : null
  const fromIso = fromMs !== null ? isoAtBuildingTz(fromMs, timeZone) : null

  useEffect(() => {
    if (!fromIso) {
      setRooms(null)
      setLoading(false)
      return
    }
    let cancelled = false
    setRooms(null)
    setLoading(true)
    setLoadError(null)
    void (async () => {
      try {
        const token = await getAccessTokenSilently()
        const res = await listFreeRooms(fromIso, FETCH_MINUTES, token)
        if (cancelled) return
        setRooms(res.rooms)
        setBuildingOpen(res.buildingOpen)
      } catch (err) {
        if (!cancelled) setLoadError(describeSheetError(err))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
    // `t` only shapes the error text; refetch when the start or reloadKey changes.
  }, [fromIso, reloadKey, getAccessTokenSilently])

  function describeSheetError(err: unknown): string {
    return isInvalidTimeRange(err)
      ? t('dashboard.bookSheet.startPassed')
      : describeBookingError(err, t)
  }

  async function book(room: FreeRoomView) {
    if (fromMs === null) return
    // `now` only ticks once a minute (less in a background tab), so "Now" is
    // recomputed from the real clock; other modes refuse a start in the past.
    let startMs = fromMs
    if (mode === 'now') {
      const fresh = nowStart(timeZone, new Date())
      if (!fresh) {
        setFeedback({ kind: 'error', message: t('dashboard.bookSheet.closed') })
        return
      }
      startMs = wallClockToUtc(fresh.ymd, fresh.hm, timeZone).getTime()
    } else if (startMs <= Date.now()) {
      setFeedback({ kind: 'error', message: t('dashboard.bookSheet.startPassed') })
      return
    }
    const startAt = isoAtBuildingTz(startMs, timeZone)
    setBookingRoomId(room.id)
    setFeedback(null)
    try {
      const token = await getAccessTokenSilently()
      const dto = await createMyBooking(
        { roomId: room.id, startAt, endAt: isoAtBuildingTz(startMs + minutes * 60_000, timeZone) },
        token,
      )
      onBooked(dto)
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        // Exchange declined (someone booked first, or a meeting made in
        // Outlook that D1 doesn't know about): mark the room busy for this
        // start + duration and refresh the rest of the list.
        const key = declinedKey(startAt, minutes)
        setDeclined((d) => ({
          key,
          roomIds: d.key === key ? [...d.roomIds, room.id] : [room.id],
        }))
        setFeedback({ kind: 'conflict' })
        setReloadKey((k) => k + 1)
      } else {
        setFeedback({ kind: 'error', message: describeSheetError(err) })
      }
    } finally {
      setBookingRoomId(null)
    }
  }

  // Declined rooms only count for the start + duration they were declined
  // for; changing either clears them.
  const declinedIds =
    fromIso && declined.key === declinedKey(fromIso, minutes) ? declined.roomIds : []
  const shownRooms =
    rooms?.map((r) => (declinedIds.includes(r.id) ? { ...r, free: false, freeUntil: null } : r)) ??
    null

  const sorted = shownRooms && fromMs !== null ? sortRoomsByFit(shownRooms, fromMs, minutes) : []
  const fitCount = sorted.filter((r) => r.fit.kind === 'fits').length
  const startHm = start?.hm ?? ''
  const endHm =
    fromMs !== null
      ? formatHourMinute(new Date(fromMs + minutes * 60_000).toISOString(), timeZone)
      : ''
  const closedDay = mode === 'date' && dateYmd !== '' && dateSlots.length === 0
  const noTimeLeftToday = mode !== 'date' && !start
  const noDatePicked = mode === 'date' && dateYmd === ''

  const whenOptions: Array<{ id: WhenMode; label: string }> = [
    { id: 'now', label: t('dashboard.bookSheet.now') },
    { id: 'later', label: t('dashboard.bookSheet.laterToday') },
    { id: 'date', label: t('dashboard.bookSheet.pickDate') },
  ]

  return (
    <div className="home-sheet__stack">
      <fieldset className="home-fieldset">
        <legend className="home-field-label">{t('dashboard.bookSheet.when')}</legend>
        <Tabs
          className="home-tabs"
          listClassName="home-tabs__list"
          tabs={whenOptions.map((o) => ({
            value: o.id,
            label: o.label,
            className: 'home-tabs__tab',
          }))}
          value={mode}
          onValueChange={(v) => {
            setMode(v as WhenMode)
            setFeedback(null)
          }}
        />
        {mode === 'later' && laterSlots.length > 0 && (
          <Select
            label={t('dashboard.bookSheet.startTime')}
            className="home-control"
            value={laterValue}
            onValueChange={(v) => setLaterHm(String(v))}
          >
            {laterSlots.map((hm) => (
              <Select.Option key={hm} value={hm}>
                {hm}
              </Select.Option>
            ))}
          </Select>
        )}
        {mode === 'date' && (
          <div className="home-field-row">
            <div className="home-field">
              {/* Native date input (Kumo `Input`), as on the mobile enroll form:
                  iOS/Android show their own picker, which beats a calendar popover
                  inside a bottom sheet. */}
              <Input
                type="date"
                label={t('dashboard.bookSheet.date')}
                className="home-control"
                value={dateYmd}
                min={tomorrow}
                max={addDaysInTz(today, MAX_DAYS_AHEAD, timeZone)}
                onChange={(e) => setDateYmd(e.target.value)}
              />
            </div>
            <div className="home-field">
              <Select
                label={t('dashboard.bookSheet.startTime')}
                className="home-control"
                value={dateValue}
                disabled={dateSlots.length === 0}
                onValueChange={(v) => setDateHm(String(v))}
              >
                {dateSlots.map((hm) => (
                  <Select.Option key={hm} value={hm}>
                    {hm}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </div>
        )}
      </fieldset>

      <fieldset className="home-fieldset">
        <legend className="home-field-label">
          {startHm
            ? t('dashboard.bookSheet.howLongStarts', { time: startHm })
            : t('dashboard.bookSheet.howLong')}
        </legend>
        <Tabs
          className="home-tabs"
          listClassName="home-tabs__list"
          tabs={DURATIONS.map((m) => ({
            value: String(m),
            label: formatDuration(m, t),
            className: 'home-tabs__tab',
          }))}
          value={String(minutes)}
          onValueChange={(v) => setMinutes(Number(v))}
        />
      </fieldset>

      {feedback?.kind === 'conflict' && (
        <Banner variant="error">{t('dashboard.bookSheet.conflict')}</Banner>
      )}
      {feedback?.kind === 'error' && <Banner variant="error">{feedback.message}</Banner>}

      <section
        className="flex flex-col gap-2"
        aria-labelledby="book-sheet-rooms"
        aria-busy={loading}
      >
        <h3 id="book-sheet-rooms" className="home-field-label">
          {fitCount === 1
            ? t('dashboard.bookSheet.roomsFitOne')
            : t('dashboard.bookSheet.roomsFit', { count: fitCount })}
        </h3>
        {noDatePicked ? (
          <p className="text-sm text-kumo-subtle">{t('dashboard.bookSheet.pickDateHint')}</p>
        ) : closedDay || noTimeLeftToday || (rooms && !buildingOpen) ? (
          <p className="text-sm text-kumo-subtle">{t('dashboard.bookSheet.closed')}</p>
        ) : loadError ? (
          <Banner variant="error">{loadError}</Banner>
        ) : rooms === null ? (
          <div className="flex justify-center py-4">
            <Loader />
          </div>
        ) : sorted.length === 0 ? (
          <p className="text-sm text-kumo-subtle">{t('dashboard.bookSheet.noRooms')}</p>
        ) : (
          <ul className="home-room-list">
            {sorted.map(({ room, fit }) => (
              <li
                key={room.id}
                className={cn('home-room', fit.kind !== 'fits' && 'home-room--unavailable')}
              >
                <div className="home-row__text">
                  <span className="home-row__title">{room.name}</span>
                  <span className={cn('home-row__meta', fit.kind === 'fits' && 'home-room__fits')}>
                    {fit.kind === 'fits'
                      ? t('dashboard.bookSheet.fits', {
                          start: startHm,
                          end: endHm,
                          people: room.capacity,
                        })
                      : fit.kind === 'partial'
                        ? t('dashboard.bookSheet.onlyFreeFor', {
                            duration: formatDuration(fit.freeMinutes, t),
                          })
                        : t('dashboard.bookSheet.busy')}
                  </span>
                </div>
                {fit.kind === 'fits' && (
                  <Button
                    variant="primary"
                    className="home-room__book"
                    loading={bookingRoomId === room.id}
                    disabled={bookingRoomId !== null}
                    aria-label={t('dashboard.bookSheet.bookRoom', { room: room.name })}
                    onClick={() => void book(room)}
                  >
                    {t('dashboard.bookSheet.book')}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <RouterLink to="/my/bookings/new" className="home-sheet__link">
        {t('dashboard.bookSheet.openFullCalendar')}
      </RouterLink>
    </div>
  )
}
